// Vault members panel (a right-side Sheet, opened from the vault view). Any
// member can view the roster; owners can add an existing user (directory
// picker), change roles (owner/editor/viewer), and remove members. Surfaces
// ROLE_CEILING / LAST_OWNER from the API as toasts.

import { useState } from "react";
import { toast } from "sonner";
import { IconUserPlus } from "@tabler/icons-react";
import {
  addMember,
  errorText,
  listMembers,
  listUsers,
  removeMember,
  setMemberRole,
  type Member,
  type UserInfo,
  type VaultRole,
} from "../api";
import { useApi } from "../session";
import { useAsyncData } from "../lib/use-async";
import { initials } from "../app/initials";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Spinner } from "@/components/ui/spinner";
import { Alert, AlertDescription } from "@/components/ui/alert";

const ROLES: VaultRole[] = ["owner", "editor", "viewer"];

export function VaultMembersSheet({
  vaultId,
  currentUserId,
  open,
  onOpenChange,
}: {
  vaultId: string;
  currentUserId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Members</SheetTitle>
          <SheetDescription>Who can see this vault, and at what role.</SheetDescription>
        </SheetHeader>
        {open && <MembersBody vaultId={vaultId} currentUserId={currentUserId} />}
      </SheetContent>
    </Sheet>
  );
}

function MembersBody({ vaultId, currentUserId }: { vaultId: string; currentUserId: string }) {
  const api = useApi();
  const { data: members, error, loading, reload } = useAsyncData(
    () => listMembers(api, vaultId),
    [vaultId],
  );

  const myRole = members?.find((m) => m.user.id === currentUserId)?.role;
  const isOwner = myRole === "owner";

  async function changeRole(m: Member, role: VaultRole) {
    if (role === m.role) return;
    try {
      await setMemberRole(api, vaultId, m.user.id, role);
      toast.success(`${m.user.display_name} is now ${role}`);
      reload();
    } catch (e) {
      toast.error("Couldn't change role", { description: errorText(e) });
      reload();
    }
  }

  async function remove(m: Member) {
    try {
      await removeMember(api, vaultId, m.user.id);
      toast.success(`Removed ${m.user.display_name}`);
      reload();
    } catch (e) {
      toast.error("Couldn't remove member", { description: errorText(e) });
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
      {isOwner && members && (
        <AddMemberPicker vaultId={vaultId} members={members} onAdded={reload} />
      )}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {loading && !members && (
        <div className="flex items-center gap-2 p-2 text-muted-foreground">
          <Spinner className="size-4" /> <span className="font-mono text-xs">loading members…</span>
        </div>
      )}

      <ul className="flex flex-col gap-1">
        {members?.map((m) => (
          <li key={m.user.id} className="flex items-center gap-3 rounded-lg px-1 py-1.5">
            <Avatar className="size-8">
              <AvatarFallback className="text-xs">{initials(m.user.display_name)}</AvatarFallback>
            </Avatar>
            <span className="min-w-0 flex-1 truncate text-sm font-medium">
              {m.user.display_name}
              {m.user.id === currentUserId && (
                <span className="ml-1 text-xs text-muted-foreground">(you)</span>
              )}
            </span>
            {isOwner ? (
              <>
                <Select value={m.role} onValueChange={(v) => void changeRole(m, v as VaultRole)}>
                  <SelectTrigger size="sm" className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {ROLES.map((r) => (
                        <SelectItem key={r} value={r}>
                          {r}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <ConfirmDialog
                  trigger={
                    <Button variant="ghost" size="icon-sm" aria-label={`Remove ${m.user.display_name}`}>
                      <RemoveIcon />
                    </Button>
                  }
                  title={`Remove ${m.user.display_name}?`}
                  description="They'll lose access to this vault immediately."
                  confirmLabel="Remove"
                  destructive
                  onConfirm={() => remove(m)}
                />
              </>
            ) : (
              <Badge variant="secondary">{m.role}</Badge>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function AddMemberPicker({
  vaultId,
  members,
  onAdded,
}: {
  vaultId: string;
  members: Member[];
  onAdded: () => void;
}) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { data: users } = useAsyncData(() => listUsers(api), []);

  const memberIds = new Set(members.map((m) => m.user.id));
  const candidates = (users ?? []).filter((u: UserInfo) => !memberIds.has(u.id));

  async function add(u: UserInfo) {
    setBusy(true);
    try {
      await addMember(api, vaultId, u.id, "viewer");
      toast.success(`Added ${u.display_name}`, { description: "as viewer — change the role below" });
      setOpen(false);
      onAdded();
    } catch (e) {
      toast.error("Couldn't add member", { description: errorText(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Button
        variant="outline"
        className="w-full justify-start"
        render={<PopoverTrigger />}
      >
        {busy ? <Spinner /> : <IconUserPlus data-icon="inline-start" />}
        Add a member
      </Button>
      <PopoverContent className="w-(--anchor-width) p-0" align="start">
        <Command>
          <CommandInput placeholder="Search accounts…" />
          <CommandList>
            <CommandEmpty>No accounts to add.</CommandEmpty>
            <CommandGroup>
              {candidates.map((u) => (
                <CommandItem key={u.id} value={u.display_name} onSelect={() => void add(u)}>
                  <Avatar className="size-6">
                    <AvatarFallback className="text-[0.625rem]">
                      {initials(u.display_name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="flex-1 truncate">{u.display_name}</span>
                  <Badge variant="secondary" className="font-mono text-[0.625rem]">
                    {u.server_role}
                  </Badge>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function RemoveIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
      <path d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M7 7l1 12a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1l1-12" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
